import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Grid, Header, Icon, Popup } from 'semantic-ui-react';
import { YouTubePlayer } from 'components';
import { ExposureChart, ExposureTable } from '../components';
import RingChart from '../../../../components/RingChart/RingChart';
import assignColors from '../../../../utils/assignColors';
import AssetLegend from '../../../../components/AssetLegend';
import { translateLabels } from '../../../../utils/helpers';

@connect((state) => ({
  french: state.Storage.language === 'fr',
  partnerName: state.Storage['partner-name'],
}))
export default class AssetClass extends Component {
  constructor(props) {
    super(props);
    this.state = {
      video: false,
    };
  }

  hasNegative = (data) => data.filter((item) => item < 0).length !== 0;

  toggleVideo = (id) =>
    this.setState((prevState) => ({
      video: id === prevState.video ? false : id,
    }));

  render() {
    const { data, french, partnerName } = this.props;
    const { video } = this.state;
    const { assetAllocationByRegion = {} } = data;
    const regionGroups = Object.entries(assetAllocationByRegion).reduce(
      (acc, [key, value]) => {
        const [parent, ...rest] = key.split(' ');

        const parentKey = parent === 'Fixed' ? 'Fixed Income' : parent;
        let regionName = rest.join(' ') || parentKey;

        if (parent === 'Fixed' && rest[0] === 'Income') {
          regionName = rest.slice(1).join(' ') || parentKey;
        }

        if (!acc[parentKey]) acc[parentKey] = [];
        acc[parentKey].push({
          region: regionName,
          value,
        });
        return acc;
      },
      {}
    );

    const assetAllocation = Object.values(data.tickersValid).reduce(
      (total, value) => {
        if (total[value.class]) {
          total[value.class] += value.weight;
        } else {
          total[value.class] = value.weight;
        }

        return total;
      },
      {}
    );
    const assetAllocationClass = {
      Alternative:
        'Alternative' in data.assetAllocation
          ? data.assetAllocation.Alternative
          : 0,
      Equity:
        'Equity' in data.assetAllocation ? data.assetAllocation.Equity : 0,
      'Fixed Income':
        'Fixed Income' in data.assetAllocation
          ? data.assetAllocation['Fixed Income']
          : 0,
      Cash: 'Cash' in data.assetAllocation ? data.assetAllocation.Cash : 0,
      Derivatives:
        'Misc' in data.assetAllocation ? data.assetAllocation.Misc : 0,
    };
    const riskContClass = {
      Alternative:
        'Alternative' in data.riskCont.class
          ? data.riskCont.class.Alternative
          : 0,
      Equity: 'Equity' in data.riskCont.class ? data.riskCont.class.Equity : 0,
      'Fixed Income':
        'Fixed Income' in data.riskCont.class
          ? data.riskCont.class['Fixed Income']
          : 0,
      Cash: 'Cash' in data.riskCont.class ? data.riskCont.class.Cash : 0,
      'Asset Allocation':
        'Asset Allocation' in data.riskCont.class
          ? data.riskCont.class['Asset Allocation']
          : 0,
    };

    const customOrder = [
      'Alternative',
      'Equity',
      'Fixed Income',
      'Asset Allocation',
      'Cash',
    ];
    const sortedData = customOrder.map((key) => ({
      ticker: key,
      assetAllocation: assetAllocation[key] || 0,
      riskCont: riskContClass[key] || 0,
      retCont: data.retCont.class[key] || 0,
    }));
    const customSortOrder = (a, b) =>
      customOrder.indexOf(a.ticker) - customOrder.indexOf(b.ticker);

    sortedData.sort(customSortOrder);
    const filteredAssetAllocationClass = Object.entries(assetAllocationClass)
      // eslint-disable-next-line no-unused-vars
      .filter(([key, value]) => value !== 0)
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, {});

    const assetAllocationValues = Object.keys(riskContClass).map((key) =>
      key in assetAllocation ? assetAllocation[key] : 0
    );

    return (
      <Container>
        <Header
          dividing
          size="large"
          content={
            french ? "Analyse de la classe d'actifs" : 'Asset Class Analysis'
          }
        />
        <Header
          size="medium"
          content={french ? 'Contribution au risque' : 'Risk Contribution'}
          subheader={french ? "Par classe d'actifs" : 'By Asset Class'}
        />
        <p>
          {french
            ? ''
            : 'Watch a short video that explains the concept of risk contribution.'}
          &nbsp;
          {!french && (
            <Icon
              name="youtube play"
              onClick={() => this.toggleVideo('xIJSNeND5bk')}
              link
              style={{ color: 'rgb(255,2,2)' }}
            />
          )}
        </p>
        {!french && (
          <YouTubePlayer isOpen={video === 'xIJSNeND5bk'} id="xIJSNeND5bk" />
        )}
        {french ? (
          <p>
            Si vous détenez des fonds qui investissent dans plus d’une catégorie
            d’actifs, veuillez noter que nous ne sommes pas en mesure de séparer
            la contribution au risque de leurs catégories d’actifs sous-jacentes
            (p. ex. la portion en actions d’un fonds équilibré), puisque les
            fonds ne publient pas les rendements par catégorie d’actifs
            individuellement.
          </p>
        ) : (
          <p>
            If you have funds that invest in more than one asset class, note
            that we are not able to separate the risk contribution of their
            underlying asset classes (e.g., the equity portion of a balanced
            fund), because funds do not report their asset class returns
            individually.
          </p>
        )}

        <Grid style={{ marginTop: '3rem', paddingBottom: '5rem' }}>
          <Grid.Row columns={2}>
            <Grid.Column
              style={{ width: '40%', flexBasis: '40%', maxWidth: '40%' }}
            >
              <Header size="medium" style={{ margin: 0 }}>
                {french ? 'Répartition des actifs' : 'Asset Allocation'}&nbsp;
                <Popup
                  trigger={
                    <Icon
                      name="question circle outline"
                      style={{
                        fontSize: '15px',
                        verticalAlign: 'initial',
                        color: 'dimgrey',
                        marginRight: '1rem',
                      }}
                    />
                  }
                  position="top center"
                  wide
                  content={
                    french
                      ? "Voir les informations importantes sur les fonds équilibrés/fonds de répartition d'actifs ci-dessus."
                      : 'See important information about balanced funds/asset allocation funds above.'
                  }
                />
              </Header>
              {this.hasNegative(assetAllocationValues) ? (
                <div className="bar">
                  <ExposureChart
                    title=""
                    labels={Object.keys(riskContClass)}
                    data={assetAllocationValues}
                  />
                </div>
              ) : (
                <div className="chart">
                  <RingChart
                    labels={translateLabels(
                      Object.keys(riskContClass),
                      french,
                      true
                    )}
                    values={assetAllocationValues}
                    maxHeight={350}
                    maxWidth={350}
                    colors={assignColors(
                      Object.keys(riskContClass),
                      partnerName
                    )}
                  />
                </div>
              )}
            </Grid.Column>

            <Grid.Column
              style={{ width: '60%', flexBasis: '60%', maxWidth: '60%' }}
            >
              <Header
                size="medium"
                content={
                  french ? 'Contribution au risque' : 'Risk Contribution'
                }
                style={{ margin: 0 }}
              />
              {this.hasNegative(Object.values(riskContClass)) ? (
                <div className="bar">
                  <ExposureChart
                    title=""
                    labels={Object.keys(riskContClass)}
                    data={Object.values(riskContClass)}
                  />
                </div>
              ) : (
                <div className="chart">
                  <RingChart
                    labels={translateLabels(
                      Object.keys(riskContClass),
                      french,
                      true
                    )}
                    values={Object.values(riskContClass)}
                    maxHeight={350}
                    maxWidth={350}
                    colors={assignColors(
                      Object.keys(riskContClass),
                      partnerName
                    )}
                  />
                </div>
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <ExposureTable
          headers={[
            <div>
              {french ? 'Classe' : 'Class'}
              <Popup
                trigger={
                  <Icon
                    name="question circle outline"
                    style={{
                      fontSize: '15px',
                      verticalAlign: 'initial',
                      color: 'dimgrey',
                    }}
                  />
                }
                position="top center"
                wide
                content={
                  french
                    ? 'Classification des titres/niveau de fonds'
                    : 'Security / fund level classification'
                }
              />
            </div>,
            french ? 'Répartition' : 'Allocation',
            <div>
              {french ? 'Contribution au risque' : 'Risk Contribution'}
              <Popup
                trigger={
                  <Icon
                    name="question circle outline"
                    style={{
                      fontSize: '15px',
                      verticalAlign: 'initial',
                      color: 'dimgrey',
                    }}
                  />
                }
                position="top center"
                wide
                content={
                  french
                    ? "Une contribution négative au risque se produit lorsqu'un actif à faible volatilité a une corrélation négative avec les autres actifs. (En termes statistiques, lorsqu'un actif a une petite variance par rapport à sa covariance avec les autres actifs.) Cela peut également se produire si vous avez une position courte dans le portefeuille."
                    : 'A negative risk contribution happens when a low volatility asset has a negative correlation with the other asset(s). (In statistical terms, when an asset has a small variance relative to its covariance with the other asset(s).) It can also happen if you have a short position in the portfolio.'
                }
              />
            </div>,
            french ? 'Contribution au rendement' : 'Return Contribution',
          ]}
          totals={[
            french ? 'Total' : 'Total',
            <div>100%</div>,
            <div>100%</div>,
            <div>100%</div>,
          ]}
          data={sortedData}
          assetClass
          fullWidth
        />

        <div style={{ paddingTop: '3rem', paddingBottom: '5rem' }}>
          <Header
            size="medium"
            content={
              french
                ? 'Répartition des titres sous-jacents'
                : 'Underlying Holdings Breakdown'
            }
            subheader={
              french
                ? 'Par catégorie d’actifs, région et structure'
                : 'By Asset Class, Region, and Structure'
            }
          />

          <p style={{ marginTop: '1rem' }}>
            {french
              ? 'Si vous détenez des fonds de répartition d’actifs ou des fonds équilibrés, ce graphique tient compte de la répartition d’actifs des positions sous-jacentes. '
              : 'If you have any asset allocation or balanced funds, this chart accounts for the asset mix of the underlying holdings. '}
            {french
              ? 'Par exemple, si vous investissez 50 % de votre portefeuille dans un fonds 60/40 et 50 % dans un fonds 80/20, votre répartition nette de l’actif est alors de 70/30.'
              : 'For example, if you put 50% of your money in a 60/40 fund and 50% in an 80/20 fund, then your net asset allocation is 70/30.'}
            <br />
            <br />
            {french
              ? 'Veuillez noter que i) certains fonds d’actions peuvent détenir un faible pourcentage de titres à revenu fixe à court terme ou de produits dérivés, et ii) les FNB synthétiques afficheront 100 % de produits dérivés, puisque leurs titres sous-jacents sont des contrats de swap.'
              : 'Note that i) some equity funds may have a small percentage of short-term fixed income securities or derivatives, and ii) synthetic ETFs will show 100% derivatives as their underlying holdings are swap contracts.'}
          </p>
        </div>
        <div>
          <Grid style={{ paddingBottom: '3rem' }}>
            <Grid.Row columns={2}>
              <Grid.Column
                style={{ width: '40%', flexBasis: '40%', maxWidth: '40%' }}
              >
                {this.hasNegative(
                  Object.values(filteredAssetAllocationClass)
                ) ? (
                  <div className="bar">
                    <ExposureChart
                      title=""
                      labels={Object.keys(filteredAssetAllocationClass)}
                      data={Object.values(filteredAssetAllocationClass)}
                    />
                  </div>
                ) : (
                  <div className="chart">
                    <RingChart
                      labels={translateLabels(
                        Object.keys(filteredAssetAllocationClass),
                        french,
                        true
                      )}
                      values={Object.values(filteredAssetAllocationClass)}
                      maxHeight={350}
                      maxWidth={350}
                      colors={assignColors(
                        Object.keys(filteredAssetAllocationClass),
                        partnerName
                      )}
                    />
                  </div>
                )}
              </Grid.Column>
              <Grid.Column>
                <div className="table">
                  <AssetLegend
                    data={filteredAssetAllocationClass}
                    french={french}
                    diversification
                    partnerName={partnerName}
                    regionAssets={regionGroups}
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      </Container>
    );
  }
}

AssetClass.propTypes = {
  data: PropTypes.object,
  french: PropTypes.bool,
  partnerName: PropTypes.string,
};

AssetClass.defaultProps = {
  data: {},
  french: false,
  partnerName: '',
};

const Container = styled.div`
  padding: 2rem 2rem 1rem 2rem;

  .ringchart-container-parent {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    column-gap: 1rem;
    height: 100%;
    width: 100%;
    padding-right: 10%;

    .ringchart-container {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .table {
      .item {
        display: flex;
        flex-direction: row;
        align-items: start;
        column-gap: 5px;
      }
    }
  }

  .chart {
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
    padding: 0rem 5rem;
  }
  .bar {
    margin: 0;
    padding-right: 20%;
  }
`;
